import { useEffect, useState } from 'react';
import { adminApi } from '../lib/api-client';
import { setTzPreference } from '../lib/time-format';
import { setPreferredZone } from '../lib/zones';

/**
 * Fetch the caller's own staff record (role + staffId + freshness) via
 * `GET /admin/me`. The role is required by every 3c portal screen so it can
 * hide destructive options for `support` callers (ADR-013 § Per-action
 * permission tier). Role is loaded from DynamoDB per request server-side
 * (ADR-009) — the JWT does not carry it.
 *
 * Refresh-on-mount only: stale role data closes the gap on the next page
 * load. If the server upgrades a staff member's role mid-session, the
 * portal continues to show the cached (smaller) set of options until the
 * next /admin/me call. Per kickoff brief § Anticipated edge cases #12 this
 * is acceptable — the server is always the authoritative gate.
 */
export type StaffRole = 'super_admin' | 'nexa_admin' | 'support' | 'read_only' | 'audit_only';

export interface StaffMe {
  staffId: string;
  role: StaffRole;
  isFreshAuth: boolean;
  /** Display name — the DDB `displayName` (My Profile, editable) preferred over
   *  the Cognito `name` claim; surfaced in the navbar so operators can see whose
   *  session they're using. Optional. */
  name?: string;
  /** telemetry-uat2 — My Profile fields. tzPreference (IANA zone, null ⇒ device)
   *  drives the shared time formatter; displayName/email populate the form. */
  tzPreference?: string | null;
  /** ADR-123 Decision 3 — the zone code every zone control pre-selects; null ⇒ platform home. */
  preferredZone?: string | null;
  displayName?: string | null;
  email?: string | null;
}

export type StaffMeState =
  | { status: 'loading' }
  | { status: 'loaded'; me: StaffMe }
  | { status: 'error'; message: string };

export function useStaffMe(): StaffMeState {
  const [state, setState] = useState<StaffMeState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await adminApi<StaffMe>('/admin/me');
        if (!cancelled) {
          // telemetry-uat2 — seed the shared time formatter's zone preference.
          setTzPreference(me.tzPreference ?? null);
          // ADR-123 — seed the zone controls' default the same way.
          setPreferredZone(me.preferredZone ?? null);
          setState({ status: 'loaded', me });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Could not load staff profile.',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Mutating tier per ADR-012 — can create / update / mutate. */
export function isMutating(role: StaffRole): boolean {
  return role === 'super_admin' || role === 'nexa_admin';
}

/** Role permitted to perform destructive subscription actions (suspend / cancel
 *  / past_due) per ADR-013 § Per-action permission tier. `support` may only
 *  reactivate (`* → active`). */
export function canDoDestructiveSubscriptionAction(role: StaffRole): boolean {
  return role === 'super_admin' || role === 'nexa_admin';
}
